import type { Lote } from "@plantus/api-client";

/**
 * A legenda da tabela — e ela só afirma o que for verdade na lista.
 *
 * <p>Saiu de `PaginaLotes.tsx` no recorte do ADR-0013 §2, sem uma vírgula de
 * diferença: o texto que ela monta é o mesmo, e `ordemDosLotes.teste.ts` e
 * `painelDenso.teste.tsx` continuam julgando as mesmas frases.
 *
 * <p>"todos em quarentena até haver assinatura" era texto fixo. É verdade hoje,
 * porque não há caminho para sair da quarentena, e vira mentira em silêncio no
 * primeiro lote liberado: a mesma classe de defeito do tom fixo no selo, uma
 * linha acima na tela. Afirmação sobre o conjunto se calcula do conjunto.
 *
 * <p>No consolidado ela diz de <b>quantas empresas</b> a lista é — calculado da
 * lista, pelo mesmo princípio. "12 lotes" sem dizer de quantos CNPJs é o número
 * que a diretoria lê como se fosse de um.
 *
 * <p>🔴 E ela diz quando a lista está <b>RECORTADA</b>. A faixa de filtros fica
 * fechada por decisão de densidade, e a pior consequência possível de um filtro
 * escondido é alguém concluir <i>"não há material vencido"</i> sobre uma lista
 * que ela mesma recortou por período meia hora antes. São duas afirmações no
 * mesmo lugar em que o total já se lê — não custam linha nenhuma.
 *
 * <p>🔴 <b>E ela só afirma recorte quando houve recorte.</b> A contagem de
 * filtros incluía a ORDEM, e um clique em "Validade" sem filtro nenhum fazia
 * esta frase dizer <i>"lista recortada por 1 filtro(s)"</i> sobre a lista
 * inteira. Afirmar um recorte que não houve é a mesma falha que esta legenda
 * existe para impedir, virada do avesso — e uma legenda que exagera é uma
 * legenda que se aprende a ignorar.
 *
 * <p>A ordem é dita <b>como ordem</b>, logo depois: <i>"ordenada por Validade,
 * crescente"</i>. Ela não some da tela — muda de frase, e ganha a única forma
 * de aparecer no PAPEL, onde a seta do cabeçalho não vai.
 */
export function legendaDosLotes(
  lotes: readonly Lote[],
  consolidado: boolean,
  filtros: number,
  ordenacao: string | null,
): string {
  const total = `${lotes.length} lote(s)`;
  const empresas = new Set(lotes.map((l) => l.empresaId)).size;
  const partes = [total];
  if (consolidado) partes.push(`${empresas} empresa(s)`);
  if (filtros > 0) partes.push(`lista recortada por ${filtros} filtro(s)`);
  if (ordenacao !== null) partes.push(ordenacao);
  /*
   * ⚠️ "todos em quarentena" continua sendo calculado da lista carregada, e
   * ela pode estar FILTRADA por situação. A afirmação segue verdadeira sobre o
   * que está na tela — que é o que ela sempre afirmou —, e a parte acima diz
   * que o que está na tela é um recorte. Sem ela, a frase viraria uma
   * afirmação sobre o estoque inteiro tirada de um filtro.
   */
  if (lotes.every((l) => l.situacao === "QUARENTENA")) {
    partes.push("todos em quarentena até haver assinatura");
  }
  return partes.join(" · ");
}
